import { MutableRefObject, useEffect, useState } from 'react';

type UseIsOverflowingParams = {
    elRef: MutableRefObject<HTMLElement | null>;
    deps?: unknown[];
    /**
     * Следить за изменением размера элемента (ResizeObserver)
     * По умолчанию: true
     */
    observeResize?: boolean;
    /**
     * Следить за изменением размера окна
     * По умолчанию: true
     */
    observeWindowResize?: boolean;
};

export const useIsOverflowing = ({
    elRef,
    deps = [],
    observeResize = true,
    observeWindowResize = true,
}: UseIsOverflowingParams) => {
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            if (elRef.current) {
                const isOverflow =
                    elRef.current.scrollWidth > elRef.current.clientWidth ||
                    elRef.current.scrollHeight > elRef.current.clientHeight;
                setIsOverflowing(isOverflow);
            }
        };

        checkOverflow();

        let resizeObserver: ResizeObserver | undefined;
        const node = elRef.current;
        if (observeResize && node && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(checkOverflow);
            resizeObserver.observe(node);
        }

        if (observeWindowResize) {
            window.addEventListener('resize', checkOverflow);
        }

        return () => {
            if (observeWindowResize) {
                window.removeEventListener('resize', checkOverflow);
            }
            if (resizeObserver && node) {
                resizeObserver.unobserve(node);
                resizeObserver.disconnect();
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elRef, observeResize, observeWindowResize, ...deps]);

    return { isOverflowing };
};
